import React, { useContext } from "react";
import { Card, CardContent, Typography, Grid, Button, Box, AppBar, Link, IconButton } from "@mui/material";
import MainNavigation from "../MainNavigation/MainNavigation";
import CpdStyledHeader from "./CpdStyledHeader";
import cpdDiabetes from "../../assets/cpd-diabetes.png";
import cpdSkin from "../../assets/cpd-skin.png";
import cpdCytology from "../../assets/cpd-cytology.png";
import theme from "../theme";
import authService from "../../services/authService";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client/react";
import { useEffect, useState } from "react";
import styles from "./CPDprograms.module.css";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import Loading from "../Loading/Loading";
import AppContext from "../../context/AppContext";
import { PDFDocument } from "pdf-lib";
import dayjs from "dayjs";
import { GetCpdProgramQuery } from "./types/GetCpdProgramQuery";
import getCpdProgramQuery from "./GetCpdProgramQuery";
import { GetCompletedCertificatesQuery } from "./types/GetDoctorCertificateQuery";
import getDoctorCertificateQuery from "./GetDoctorCertificateQuery";
import { MarkProgramAsCompleteMutation } from "./types/MarkProgramAsCompleteMutation";
import markProgramAsCompleteMutation from "./MarkProgramAsCompleteMutation";
import tz from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
dayjs.extend(tz);
dayjs.extend(utc);

export type CPDprogramsProps = {
  display?: string;
  ml?: number;
  style?: React.CSSProperties;
};

const createStyles = () => ({
  header: {
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },
  header2: {
    color: theme.palette.primary.main,
  },
  welcomeLogo: {
    width: "300px",
  },
  pointer: {
    cursor: "pointer",
  },
  container: {
    position: "relative" as const,
  },
  shape: {
    borderStyle: "solid",
    msTransform: "rotate(360deg)",
    OTransform: "rotate(360deg)",
    WebkitTransform: "rotate(360deg)",
    transform: "rotate(360deg)",
    borderColor: "transparent " + theme.palette.primary.dark + " transparent transparent",
    float: "right" as const,
    right: "0px",
    height: "0px",
    width: "0px",
    borderWidth: "0 120px 120px 0",
    position: "absolute" as const,
    display: "flex",
  },
  shapeText: {
    transform: "rotate(45deg)",
    color: "#FFF",
    fontSize: "20px",
    fontWeight: "bold",
    position: "relative" as const,
    right: "-60px",
    top: "5px",
    textAlign: "center" as const,
    margin: "auto",
  },
});

const CPDprogramsPage: React.FC<CPDprogramsProps> = () => {
  const classes = createStyles();
  const navigate = useNavigate();
  const mobility = authService.isMobility();
  const offsetHeight = mobility ? 161 : 138;
  const isDesktop = useContext(AppContext).isDesktop;

  const cpdPanel = authService.getCPDEnrolled();

  const { data, loading } = useQuery<GetCpdProgramQuery>(getCpdProgramQuery);

  const { data: certificateData } = useQuery<GetCompletedCertificatesQuery>(getDoctorCertificateQuery, {
    variables: {
      username: localStorage.getItem("username"),
    },
  });

  const [markProgramAsComplete] = useMutation<MarkProgramAsCompleteMutation>(markProgramAsCompleteMutation);

  const [cpdPrograms, setCpdPrograms] = useState<GetCpdProgramQuery['getCpdProgram']>([]);

  const [doctorCertificates, setDoctorCertificates] = useState<GetCompletedCertificatesQuery['getCompletedCertificates']>([]);

  const newsletterUrl = "https://www.clinicallabs.com.au/doctor/gp-services/newsletters/";

  const handleDownloadMultiple = async (certificates: GetCompletedCertificatesQuery['getCompletedCertificates']) => {
    try {
      // Create a new PDF document for merging
      const mergedPdf = await PDFDocument.create();

      // Process each certificate and add to merged PDF
      // https://jira.pl.pathlabs.com.au/browse/CLSD-431115 Only return the first certificate for now 
      let noCerts = 0;

      for (const data of certificates) {
        if (noCerts === 0) {
          const programname = data.programname || "";
          let pdfFile = "diabetes";
          let nameInput = "Dr FULL NAME";
          let racgpInput = "Dr's RACGP Number";
          const racgpValueInput = "RACGP";
          const acrrmValueInput = "ACRRM";
          const certificatedateInput = "Date";
          let footerTextInput = "Text3";
          let certDate = dayjs();
  
          if (data.certificatedate) {
            //check if data.year is lower than data.certificatedate year
            if (data.year && data.year < dayjs(data.certificatedate).year()) {
              certDate = dayjs.tz(`${data.year}-12-31`, 'Australia/Victoria');
            } else {
              certDate = dayjs(data.certificatedate);
            }
          } else if (data.year && data.year < dayjs().year()) {
            certDate = dayjs.tz(`${data.year}-12-31`, 'Australia/Victoria');
          }
  
          const certDateStr = certDate.tz('Australia/Victoria').format('DD/MM/YYYY');
          let footerText = `Allocated 10 CPD Measuring Outcomes hours and 10 CPD Reviewing Performance hours for the ${certDate.year()} year.`;
  
          if (programname.toLowerCase().includes("skin")) {
            pdfFile = "skin";
            nameInput = "Name 1";
            racgpInput = "Name 2";
            footerTextInput = "Text1";
            footerText = `Allocated 20 CPD Measuring Outcomes hours + 5 CPD Reviewing Performance hours + 2.5 CPD Educational Activities hours for the ${certDate.year()} year.`;
          } else if (programname.toLowerCase().includes("cytology")) {
            pdfFile = "cytology";
            footerTextInput = "Text3";
            footerText = `Allocated 20 CPD Measuring Outcomes hours, 5 CPD Reviewing Performance hours and 5 Education Activity hours for the ${certDate.year()} year.`;
          }
  
          // Load the template PDF
          const formUrl = data.pdf_template
            ? `https://ehealth-service-static-files.s3.ap-southeast-2.amazonaws.com/media/${data.pdf_template}`
            : `https://ehealth-service-static-files.s3.ap-southeast-2.amazonaws.com/media/cpd-certificate-${pdfFile}.pdf`;
          const formPdfBytes = await fetch(formUrl).then((res) => res.arrayBuffer());
          const pdfDoc = await PDFDocument.load(formPdfBytes);
  
          // Fill the form
          const form = pdfDoc.getForm();
          const nameField = form.getTextField(nameInput);
          const racgpField = form.getTextField(racgpInput);
          const racgpValueField = form.getTextField(racgpValueInput);
          const acrrmValueField = form.getTextField(acrrmValueInput);
          const certificatedateField = form.getTextField(certificatedateInput);
          const text3Input = form.getTextField(footerTextInput);
  
          nameField.setText(data?.fullname ? (data.fullname as string) : data.username || "");
          const membership: string[] = [];
          if (data.racgp) membership.push(`RACGP: ${data.racgp}`);
          if (data.acrrm) membership.push(`ACCRM: ${data.acrrm}`);
          if (data.other_membership) membership.push(`Other: ${data.other_membership}`);
          racgpField.setText(membership.join(", "));
          racgpValueField.setText((data.racgp_activity_num as string) || "N/A");
          acrrmValueField.setText((data.acrrm_activity_num as string) || "N/A");
          certificatedateField.setText(certDateStr);
          text3Input.setText(footerText);
          form.flatten();
  
          // Copy pages to merged PDF
          const pages = await mergedPdf.copyPages(pdfDoc, pdfDoc.getPageIndices());
          pages.forEach((page) => mergedPdf.addPage(page));
        }

        noCerts++;
      }

      // Save and download the merged PDF
      const mergedPdfBytes = await mergedPdf.save();
      const blob = new Blob([mergedPdfBytes as BlobPart], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Combined Certificates ${certificates[0]?.programname || "all"}.pdf`;
      link.click();

      const cpeId = Number(certificates[0]?.cpe_id);

      await markProgramAsComplete({ variables: { cpeId: cpeId }});
    } catch (error) {
      console.error("Error generating merged certificates:", error);
    }
  };

  useEffect(() => {
    if (data) {
      setCpdPrograms(data.getCpdProgram);
    }
  });

  useEffect(() => {
    if (certificateData) {
      setDoctorCertificates(certificateData.getCompletedCertificates);
    }
  });

  const onOpen = (programId: number, mainUrl: string) => {
    let url = "/cpd-programs";

    switch (programId) {
      case 1:
        url = "/cpd-diabetes";
        break;
      case 2:
        url = "/cpd-skin";
        break;
      case 3:
        url = "/cpd-cytology";
        break;
      default:
        window.open(mainUrl);
        return;
    }
    navigate(url);
  };

  return (
    <>
      <AppBar elevation={0} className={styles.headerBar}>
        <MainNavigation
          subHeader="SELECT YOUR CPD PROGRAM"
          showBackButton
          mainHeader={CpdStyledHeader()}
          backText="PATIENTS"
          backPath="/patients"
          hideProviders={true}
          isCpd
          displayACL={isDesktop ? true : false}
        />
      </AppBar>
      <div style={{ height: offsetHeight }} />
      <Box mt={5}>
        {loading ? (
          <Loading />
        ) : (
          <Grid container spacing={5} justifyContent={"center"} alignItems="center">
            <Grid
              size={{
                xs: 12,
                md: 12,
                lg: 10,
                xl: 8,
              }}
            >
              <Grid container spacing={5} justifyContent={"space-evenly"} alignItems={"center"}>
                {cpdPrograms.map((elem) => {
                  const matchingCertificate = doctorCertificates?.filter((cert) => cert.programname === elem.name);
                  return (
                    <Grid
                      key={cpdPrograms.indexOf(elem)}
                      size={{
                        xs: 12,
                        md: 6,
                        lg: 4,
                      }}
                    >
                      <Typography
                        variant="h4"
                        textAlign={"center"}
                        sx={{ ...classes.header, ...classes.pointer }}
                        onClick={() =>
                          onOpen(
                            cpdPanel.includes(elem.id) ? elem.id : 0,
                            elem.url || "https://www.clinicallabs.com.au/",
                          )
                        }
                      >
                        {elem.short_name}
                      </Typography>
                      <Typography
                        variant="h4"
                        textAlign={"center"}
                        sx={{ ...classes.header2, ...classes.pointer }}
                        onClick={() =>
                          onOpen(
                            cpdPanel.includes(elem.id) ? elem.id : 0,
                            elem.url || "https://www.clinicallabs.com.au/",
                          )
                        }
                      >
                        Evaluation Program
                      </Typography>
                      <Box textAlign={"center"}>
                        <Card
                          style={{
                            marginTop: "10px",
                            borderRadius: "25px 25px 4px 4px",
                            display: "inline-block",
                            width: "auto",
                          }}
                        >
                          <CardContent
                            style={{ padding: "0px", margin: "0px" }}
                            onClick={() =>
                              onOpen(
                                cpdPanel.includes(elem.id) ? elem.id : 0,
                                elem.url || "https://www.clinicallabs.com.au/",
                              )
                            }
                          >
                            <div style={classes.container}>
                              <div style={classes.shape}>
                                <div style={classes.shapeText}>{`${elem.hours} Hours`}</div>
                              </div>
                              <img
                                src={elem.id === 1 ? cpdDiabetes : elem.id == 2 ? cpdSkin : cpdCytology}
                                alt="acl logo"
                                style={{ ...classes.welcomeLogo, ...classes.pointer }}
                              />
                            </div>
                            <Box textAlign={"center"} m={2.5} paddingLeft={matchingCertificate.length ? "40px" : "0px"}>
                              {cpdPanel.includes(elem.id) ? (
                                <Button
                                  color="primary"
                                  variant="contained"
                                  disabled={!elem.enabled}
                                  onClick={() =>
                                    onOpen(elem.id ? elem.id : 0, elem.url || "https://www.clinicallabs.com.au/")
                                  }
                                >
                                  Open
                                </Button>
                              ) : (
                                <Button
                                  color="warning"
                                  variant="contained"
                                  disabled={!elem.enabled}
                                  target="_blank"
                                  href={elem.url || "https://www.clinicallabs.com.au/"}
                                >
                                  Register
                                </Button>
                              )}
                              {matchingCertificate.length ? (
                                <IconButton
                                  style={{ left: "70px" }}
                                  title="Download Certificate"
                                  color="primary"
                                  aria-label="Download Certificate"
                                  component="span"
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    await handleDownloadMultiple(matchingCertificate);
                                  }}
                                >
                                  <WorkspacePremiumIcon />
                                </IconButton>
                              ) : (
                                <></>
                              )}
                            </Box>
                          </CardContent>
                        </Card>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>

            <Grid key={"links"} mt={2} justifyContent={"center"} alignItems={"center"} size={12}>
              <Grid container justifyContent={"center"} alignItems="center">
                <Grid
                  textAlign={"center"}
                  size={{
                    xs: 8,
                    sm: 8,
                    md: 12,
                  }}
                >
                  <Typography sx={{ fontWeight: "bold" }}>
                    Please Note: CPD Programs are eligible for Australian Clinical Labs referrers only
                  </Typography>
                </Grid>
                <Grid
                  textAlign={"center"}
                  mt={4}
                  size={{
                    xs: 8,
                    sm: 8,
                    md: 5,
                  }}
                >
                  <Link href={newsletterUrl} target="_blank" style={{ textDecoration: "underline" }}>
                    Need more CPD Hours? Read Clinical Labs&apos; Pathology Focus and log your Educational Activities
                    hours using the RACGP MyCPD app.
                  </Link>
                  <br />
                  <Box mt={4} mb={4}>
                    <Button color="primary" variant="contained" target="_blank" href={newsletterUrl}>
                      View Newsletter Archive
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
      </Box>
    </>
  );
};
export default CPDprogramsPage;
